import { Api, ModelRow, StringUtil } from "@mcleod/core";
import { ErrorParam } from "@mcleod/core/src/ErrorHandler";
import { HorizontalSpacer, MultiButton, PanelProcessLastRun, PanelProcessLastRunVariant, ReportPreviewTabSource } from "../..";
import { DataSourceAction, DataSourceMode } from "../../databinding/DataSource";
import { DataSourceExecutionEvent } from "../../events/DataSourceExecutionEvent";
import { DecoratorProps } from "./DecoratorProps";
import { LayoutProvidedTaskDecoratorProps } from "./LayoutProvidedTaskDecoratorProps";
import { PreviewDecorator } from "./PreviewDecorator";
import { ReflectiveDialogs } from "../../base/ReflectiveDialogs";
import { ReportPreviewDataResolver } from "./ReportPreviewDataResolver";
import { Toast } from "../Toast";

/**
 * Decorator whose layout property contains task parameters. That layout will be nested inside the decorator, which
 * constructs/includes Clear/Search buttons, as well as the ReportPreview panel.
 */
export class TaskDecorator extends PreviewDecorator {
    private buttonExecute: MultiButton;
    private panelProcessLastRun: PanelProcessLastRun;

    constructor(props?: Partial<DecoratorProps>) {
        super(props);
    }

    override setupPanels(_props: Partial<DecoratorProps>) {
        super.setupPanels(_props);
        this.addProcessLogPanel(this.layoutProvidedProps.processLogDescription);
    }

    protected addPanelButtonComponents() {
        this.createExecuteButton();

        if (this.layoutProvidedProps.supportsReadOnlyPreview === false) {
            this.buttonExecute.color = "primary.reverse";
            this.buttonExecute.backgroundColor = "primary";
            this.buttonExecute.primaryButton.default = true;
            this.panelButtons.add(this.buttonResetForm, new HorizontalSpacer(), this.buttonExecute);
        } else {
            this.buttonResetForm.width = 80;
            this.buttonResetForm.captionWrap = false;
            this.buttonExecute.fillRow = true;
            this.buttonPreviewAction.fillRow = true;
            this.panelButtons.add(this.buttonResetForm, this.buttonExecute, this.buttonPreviewAction);
        }
    }

    private createExecuteButton() {
        this.buttonExecute = new MultiButton({
            id: "buttonExecute",
            color: "primary",
            borderWidth: 1,
            borderColor: "primary",
            rowBreak: false,
            width: 150
        });
        this.buttonExecute.primaryButton.setProps({
            caption: this.layoutProvidedProps.executeButtonCaption ?? "Execute",
            onClick: () => this.executeTask()
        });
        this.buttonExecute.dropdownItems = this.layoutProvidedProps.executeButtonAddlActions;
    }

    override configureSearchButton() {
        super.configureSearchButton();
        this.buttonPreviewAction.primaryButton.caption = this.layoutProvidedProps.readOnlyButtonCaption ?? "View Edit List";
    }

    public actionButtonsDoAfterLayoutLoaded() {
        super.actionButtonsDoAfterLayoutLoaded();

        // The execute action button doesn't reside in the layout, so _doAfterOutermostLayoutLoaded() will not be
        // fired by Layout.ts.  So fire it here for that components.
        this.buttonExecute._doAfterOutermostLayoutLoaded();
    }

    protected override get layoutProvidedProps(): Partial<LayoutProvidedTaskDecoratorProps> {
        return this.layout.taskDecoratorProps;
    }

    protected override mainDataSourceDoBeforeExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            this.buttonExecute.enabled = false;
            super.mainDataSourceDoBeforeExecution(event);
        }
    }

    protected override mainDataSourceDoAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            super.mainDataSourceDoAfterExecution(event);
            this.buttonExecute.enabled = true;
        }
    }

    private async executeTask() {
        const dataSource = this.layout.mainDataSource;
        if (dataSource.validate(true, "Unable to execute because data is missing or invalid.") === false || await this.promptForConfirmation() !== true) {
            return;
        }

        const row = dataSource.getSearchFilter();
        this.doBeforeExecute(false, row);

        try {
            const response = await Api.post(dataSource.url, row.data);
            this.doAfterExecute(false, response);
        } catch (error) {
            this.errorHandler(error, false);
        }
    }

    protected override errorHandler(error: ErrorParam, fromSearch: boolean){
        this.doAfterExecute(fromSearch, null);
        super.errorHandler(error, fromSearch);
    }

    private promptForConfirmation(): Promise<boolean> {
        const title = this.layoutProvidedProps.confirmationDialogTitle ?? "Confirm Task Execution";
        const message = this.layoutProvidedProps.confirmationDialogMessage ?? "Are you sure you want to execute this task?";
        return ReflectiveDialogs.showYesNo(message, title);
    }

    private doBeforeExecute(search: boolean, row?: ModelRow) {
        if (search) {
            this.buttonExecute.enabled = false;
        } else {
            this.layout.mainDataSource.setRowsAndMode(DataSourceMode.NONE, []);
            this.layoutProvidedProps.doBeforePost?.(row);
            this.panelPreview.beginSearch();
            this.buttonExecute.busy = true;
            this.buttonPreviewAction.enabled = false;
        }
    }

    private doAfterExecute(search: boolean, response: any) {
        if (search) {
            this.buttonExecute.enabled = true;
        } else {
            this.buttonExecute.busy = false;
            this.buttonPreviewAction.enabled = true;
            let modelRow = null;
            if (response != null) {
                modelRow = new ModelRow(this.layout.mainDataSource.url, true, response?.data?.[0]);
                this.layout.mainDataSource.setRowsAndMode(DataSourceMode.NONE, [modelRow]);
                this.panelProcessLastRun?.refreshLastProcessedData();
            }
            let resolvedData: ReportPreviewTabSource[];
            if (this.layoutProvidedProps.adjustReportPreviewDataAfterPost) {
                resolvedData = new ReportPreviewDataResolver().resolve(modelRow.get("reports"));
                this.layoutProvidedProps.adjustReportPreviewDataAfterPost(resolvedData, response);
            }
            this.panelPreview.endSearch(resolvedData ?? modelRow);
            this.layoutProvidedProps.doAfterPost?.(this.panelPreview.data, response);
            this.displayCompletionToast(response);
        }
    }

    /**
     * Conditionally displays a success toast to the user after the action has been executed
     * @param response The server response that resulted from the executed action
     */
    private displayCompletionToast(response: any) {
        const title = this.layoutProvidedProps.completionToastTitle;
        const shouldDisplayToast = !StringUtil.isEmptyString(title) &&
            (this.layoutProvidedProps.completionToastCondition?.(response) ?? true);
        if (shouldDisplayToast) {
            Toast.showSuccessToast(title, this.layoutProvidedProps.completionToastMessage,
                this.layoutProvidedProps.completionToastImageName, this.layoutProvidedProps.completionToastOptions);
        }
    }

    private addProcessLogPanel(processLogDescription: string) {
        if (StringUtil.isEmptyString(processLogDescription)) {
            return;
        }

        this.panelProcessLastRun = PanelProcessLastRun.create(processLogDescription, {
            variant: PanelProcessLastRunVariant.compact
        });

        if (this.panelProcessLastRun != null) {
            this.panelPreview.insertFirstRightHeaderTool(this.panelProcessLastRun);
        }
    }
}
